import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { formatDashboardCurrency, formatDashboardPercent } from '@/utils/crmValuesDisplay'
import type { JourneyMacroAggregate } from '@/components/Dashboard/operacaoTab.utils'

type Props = {
  steps: JourneyMacroAggregate[]
  pipelineCount: number
  valuesVisible?: boolean
}

export function OperacaoPipelineDonut({ steps, pipelineCount, valuesVisible = false }: Props) {
  const chartData = steps
    .filter((step) => step.count > 0)
    .map((step) => ({
      name: step.macro.label,
      value: step.count,
      color: step.macro.color,
      pct: step.pct,
      amount: step.value,
    }))
  const activeCount = chartData.reduce((sum, row) => sum + row.value, 0)
  const outsideFunnel = Math.max(0, pipelineCount - activeCount)

  return (
    <div className='flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div>
        <h3 className='text-base font-semibold text-text-primary'>Distribuição do pipeline</h3>
        <p className='mt-1 text-sm text-text-muted'>Negociações ativas por macro-etapa</p>
      </div>

      {activeCount > 0 && chartData.length > 0 ? (
        <>
          <div className='relative mt-4 h-[200px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey='value'
                  nameKey='name'
                  cx='50%'
                  cy='50%'
                  innerRadius={58}
                  outerRadius={82}
                  paddingAngle={2}
                  stroke='none'
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                  }}
                  formatter={(value, _name, item) => [
                    `${Number(value ?? 0)} deals · ${formatDashboardCurrency(item.payload.amount, valuesVisible)}`,
                    item.payload.name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-2xl font-bold tabular-nums text-text-primary'>{activeCount}</span>
              <span className='text-[10px] font-medium uppercase tracking-wide text-text-muted'>
                no funil
              </span>
            </div>
          </div>

          <ul className='mt-2 space-y-2.5'>
            {steps.map((step) => (
              <li key={step.macro.id} className='flex items-center justify-between gap-2 text-sm'>
                <span className='flex min-w-0 items-center gap-2 text-text-secondary'>
                  <span
                    className='h-2.5 w-2.5 shrink-0 rounded-full'
                    style={{ backgroundColor: step.macro.color }}
                    aria-hidden
                  />
                  <span className='truncate'>{step.macro.label}</span>
                </span>
                <span className='shrink-0 tabular-nums text-text-muted'>
                  {step.count} · {formatDashboardPercent(step.pct, true, 0)}
                </span>
              </li>
            ))}
          </ul>
          {outsideFunnel > 0 ? (
            <p className='mt-3 text-xs text-text-muted'>
              +{outsideFunnel} em fechamento futuro / pausado (fora do funil ativo)
            </p>
          ) : null}
        </>
      ) : (
        <div className='my-6 flex flex-1 items-center justify-center rounded-xl border border-dashed border-[var(--border)] px-4 py-10 text-center text-sm text-text-muted'>
          Pipeline vazio nesta visão.
        </div>
      )}
    </div>
  )
}
